// The Record (spec C7.4; HQ m1-06, m1-07): the header, the needs-attention line, the Steps card
// (1 Dec page with Fetch and Upload PDF, then steps 2 to 6, spec C8), and the right column with the
// insured's contact, prior term vs renewal, the policy details from AMS360, the mortgagee, the files,
// notes and the Portal log. The header has Open in AMS360 and the actions (placeholders until built).
import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useLoaderData, useLocation, useNavigate, useRevalidator } from 'react-router';
import type {
  Api,
  FetchView,
  IncreaseRule,
  LogEntry,
  Reading,
  ReadingField,
  ReadingFixField,
  RenewalDetail,
  RenewalView,
  StepsView,
} from '../auth/api';
import { useUser } from '../auth/user';
import { managesDepartment } from '../roles';
import { DecPageStep } from '../renewals/DecPageStep';
import { draftRevision } from '../renewals/email-lines';
import { FixDialog } from '../renewals/FixDialog';
import { AwaitingTag, WaitingBanner } from '../renewals/HandToAe';
import { useHandToAe } from '../renewals/use-hand-to-ae';
import { AeHandoffBanner, BackFromAeBanner } from '../renewals/AeHandoff';
import { useHandoffAnswers } from '../renewals/use-handoff-answers';
import { answerRow, canTakeOver, passRow, withMeRow } from '../renewals/ae-handoff-lines';
import { notOnCarrierSite, oneClickHand, withAeText } from '../renewals/handoff-lines';
import { FilesCard, MortgageeCard, PortalLogCard, RecordActions } from '../renewals/RecordExtras';
import { continueAtTitle, decStepKey } from '../renewals/step-lines';
import { branchTag } from '../renewals/branch-lines';
import { useAutoRefresh } from '../shell/auto-refresh';
import { RecordSteps, type StepsActions, type StepsAsk } from '../renewals/RecordSteps';
import { useReassign } from '../renewals/use-reassign';
import {
  coverageRows,
  editedTip,
  fieldsToCheck,
  fixLabels,
  fixRows,
  fixUndoFailedText,
  fixUndoneText,
  reviewLine,
  showValue,
} from '../renewals/fix-lines';
import { IncreaseBox } from '../renewals/IncreaseBox';
import { NotesCard } from '../renewals/notes';
import { renewalPremiumOf } from '../renewals/reading-lines';
import {
  attentionBanner,
  band,
  change,
  daysLabel,
  daysPill,
  departmentName,
  increaseTagText,
  increaseTooltip,
  isAutoLine,
  longDate,
  money,
  renewedTip,
  showIncreaseTag,
  statuses,
  tabLabels,
  telHref,
  when,
  type TabKey,
} from '../renewals/renewal-lines';
import { EmptyState } from '../shell/EmptyState';
import { RecordLayout, type Crumb } from '../shell/RecordLayout';
import { Tip } from '../shell/Tip';
import { Toast } from '../shell/Toast';
import { useStuck } from '../shell/use-stuck';
import {
  AlertCircleIcon,
  AlertTriangleIcon,
  CheckIcon,
  InfoIcon,
  PencilIcon,
} from '../shell/icons';

// Pages that open a Record pass { from } in the link state, so the breadcrumb and Back return there.
export type RecordFrom = 'my' | 'all' | 'deleted' | 'settings';

export type RecordLoaderData = {
  view: RenewalView | null;
  fetch: FetchView | null;
  steps?: StepsView | null;
  /** The Portal log; null when it could not be read (the Record still opens). */
  log?: LogEntry[] | null;
  /** When the loader started reading, ms: a card's own copy from before that gives way (issue 291). */
  loadedAt?: number;
};

/**
 * A card's own copy of something the loader reads too (after a save, a tick, a new note), kept with
 * the renewal's id and when it was read. It wins over the loader's until the loader reads again
 * after it, as the auto refresh does (issue 291).
 */
type Live<T> = { id: string; at: number; value: T };
const liveOr = <T,>(live: Live<T> | null, id: string, loadedAt: number, base: T): T =>
  live && live.id === id && live.at >= loadedAt ? live.value : base;

const FROM: Record<RecordFrom, Crumb> = {
  my: { label: 'My renewals', to: '/renewals/my' },
  all: { label: 'All renewals', to: '/renewals/all' },
  deleted: { label: 'Deleted records', to: '/deleted' },
  settings: { label: 'Settings', to: '/settings' },
};

export function RecordPage({ api }: { api: Api }) {
  const { role, id: meId, name: meName, initials: meInitials } = useUser();
  const { view, fetch, steps, log = null, loadedAt = 0 } = useLoaderData() as RecordLoaderData;
  // Close renewal and Reopen change the status in the header: read the Record again.
  const revalidator = useRevalidator();
  // Auto refresh (issue 291): the Record and its notes are read again on their own, so a handoff answered
  // by the AE shows without pressing refresh.
  const [notesTick, setNotesTick] = useState(0);
  useAutoRefresh(() => setNotesTick((t) => t + 1));
  const navigate = useNavigate();
  // Reassign (spec C6). A Renewal VA who moves their own record away can no longer open it, so they
  // go back to My renewals, where the toast and its Undo show (spec: "returns to My renewals").
  const reassign = useReassign(api, { id: meId, name: meName }, (done, rows) => {
    if (role === 'Renewal VA' && rows.length > 0 && done.undo) {
      void navigate('/renewals/my', { state: { reassigned: done } });
      return;
    }
    void revalidator.revalidate();
  });
  // Hand to AE (spec C6): the renewal stays here with an Awaiting AE tag, so the Record is read again.
  const hand = useHandToAe(api, () => void revalidator.revalidate());
  // The Dec page step's latest view, so "Prior term vs renewal" shows the premium once it is read.
  // Kept with the renewal's id: going from one Record to another never shows the last one's.
  const [stepView, setStepView] = useState<Live<FetchView> | null>(null);
  const renewalId = view?.renewal.id ?? null;
  // A Record always opens at the top (Yehuda, Oct 29): from a list scrolled down, from another
  // Record, or on a reload, where the browser would put the page back where it was.
  useLayoutEffect(() => {
    const was = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
    return () => {
      window.history.scrollRestoration = was;
    };
  }, [renewalId]);
  const onStepView = useCallback(
    (v: FetchView) => {
      if (renewalId) setStepView({ id: renewalId, at: Date.now(), value: v });
    },
    [renewalId],
  );
  // The steps card's latest view feeds the Mortgagee card, and each change reads the Portal log
  // again, so both show what was just done (a tick, a send, a new dec page). Kept with the id too;
  // only the newest log answer counts.
  const [liveSteps, setLiveSteps] = useState<Live<StepsView> | null>(null);
  const [liveLog, setLiveLog] = useState<Live<LogEntry[]> | null>(null);
  const logAsk = useRef(0);
  const onStepsView = useCallback(
    (v: StepsView) => {
      if (!renewalId) return;
      setLiveSteps({ id: renewalId, at: Date.now(), value: v });
      const ask = ++logAsk.current;
      const at = Date.now();
      api.renewalLog(renewalId).then(
        (entries) => ask === logAsk.current && setLiveLog({ id: renewalId, at, value: entries }),
        () => {},
      );
    },
    [api, renewalId],
  );
  // A note added or taken back: the Portal log is read again.
  const reloadLog = useCallback(() => {
    if (!renewalId) return;
    const ask = ++logAsk.current;
    const at = Date.now();
    api.renewalLog(renewalId).then(
      (entries) => ask === logAsk.current && setLiveLog({ id: renewalId, at, value: entries }),
      () => {},
    );
  }, [api, renewalId]);
  // Confirm or correct a value read from the dec page (spec C10): the dialog, and the step's view
  // the server answered with, handed back to the Dec page step so both cards show the same.
  const [fixing, setFixing] = useState<ReadingFixField | null>(null);
  const [pushed, setPushed] = useState<{ at: number; view: FetchView } | null>(null);
  const [toast, setToast] = useState<{ id: number; text: string; undoFix?: string } | null>(null);
  // The "..." menu's Close renewal and Mark steps done previously open in the Steps card (S16). Kept
  // with the renewal's id, so going to another Record never opens it there.
  const [stepsAsk, setStepsAsk] = useState<(StepsAsk & { id: string }) | null>(null);
  // Back to Check AMS360 (S16 Batch 3): done at once on the Steps card.
  const stepsActions = useRef<StepsActions>(null);
  // The header stays in place while the steps scroll under it (Yehuda, Oct 27).
  const { ref: setHead, stuck: headStuck } = useStuck();
  // The AE's Accept and Send back on the banner (spec C6): Handled by and the status may change, so
  // the Record, its log and the steps' views are read again.
  const answers = useHandoffAnswers(
    api,
    () => {
      setLiveLog(null);
      setLiveSteps(null);
      setStepView(null);
      void revalidator.revalidate();
    },
    meId,
  );
  const state = useLocation().state as { from?: RecordFrom; tab?: TabKey | null } | null;
  const from = state?.from && FROM[state.from];
  // Opened without a portal page behind it (a pasted link): the board falls back to the role's home list.
  const page = from ?? FROM[managesDepartment(role) ? 'all' : 'my'];
  // Then the tab the user was on, left out when they came from a filter or search (RecordTopBar).
  const crumbs: Crumb[] =
    from && state?.tab
      ? [page, { label: tabLabels[state.tab], to: `${page.to}?tab=${state.tab}` }]
      : [page];

  if (!view) {
    return (
      <RecordLayout crumbs={crumbs} current="Record not found" cameFromPortal={!!from}>
        <div className="row g8 wrap">
          <h1 className="ptitle">Record not found</h1>
        </div>
        <EmptyState text="This record does not exist or was deleted." />
      </RecordLayout>
    );
  }

  const r = view.renewal;
  const now = new Date();
  const current = liveOr(stepView, r.id, loadedAt, fetch);
  const stepsNow = liveOr(liveSteps, r.id, loadedAt, steps ?? null);
  const reading = current?.reading ?? null;
  const decName = current?.documents.find((d) => d.kind === 'dec_page')?.fileName ?? 'Dec page';
  const saved = (v: FetchView, text: string, undoFix?: string) => {
    setPushed({ at: Date.now(), view: v });
    setStepView({ id: r.id, at: Date.now(), value: v });
    setFixing(null);
    setToast({ id: Date.now(), text, undoFix });
    reloadLog();
  };
  // The toast's Undo after Confirm or Save correction: the value goes back to what it was.
  const undoFix = async (fixId: string) => {
    const res = await api.undoFix(r.id, fixId);
    if (res.ok) saved(res.body.fetch, fixUndoneText);
    else setToast({ id: Date.now(), text: fixUndoFailedText });
  };
  const status = statuses[r.status];
  const upcoming = band(r.daysLeft) === 'upcoming' && status.stage === 'open';
  // The renewal premium the Record shows everywhere (spec C10: a correction is used everywhere), so
  // the Increase tag and box follow a corrected premium.
  const premium = renewalPremiumOf(r, reading)?.value ?? null;
  const shownRow = { ...r, renewalPremium: premium };
  const attention = attentionBanner(r);
  const withAe = withAeText(r.handoff ?? null, meId, r.handledBy);
  // The AE's banner: a handoff waiting on them, or one they accepted (spec C7.4).
  const mine =
    r.handoff && r.handoff.to.id === meId && role === 'Account Executive' ? r.handoff : null;
  const asking = answerRow(r, meId);
  const withMe = role === 'Account Executive' ? withMeRow(r, meId) : null;
  const oneHand = oneClickHand(r, role, meId, notOnCarrierSite(current));

  return (
    <RecordLayout crumbs={crumbs} current={r.insured} cameFromPortal={!!from}>
      <div ref={setHead} className={headStuck ? 'rhead rhead-stick stuck' : 'rhead rhead-stick'}>
        <div className="col g4">
          <div className="row g8 wrap">
            <h1 className="ptitle">{r.insured}</h1>
            <span className={`pill ${status.pill}`}>{upcoming ? 'Not started' : status.label}</span>
            <AwaitingTag h={r.handoff} meId={meId} />
            {showIncreaseTag(shownRow, view.increaseRule) && (
              <Tip text={increaseTooltip(shownRow, view.increaseRule)} focusable>
                <span className="tag t-amber">{increaseTagText(shownRow)}</span>
              </Tip>
            )}
            {r.renewedInAmsAt && (
              <Tip text={renewedTip(r.renewedInAmsAt, now)} focusable>
                <span className="tag t-blue">Renewed in AMS360</span>
              </Tip>
            )}
            {r.inactiveInAmsSince && <span className="tag t-grey">No longer active in AMS360</span>}
            {/* Closed by the System from Rachy's renewal sheet; Reopen removes it. */}
            {r.fromSheet && <span className="tag t-grey">From sheet</span>}
            {/* On a branch (spec C4, C8, S17): yellow Non-renewal with its date, grey Policy inactive. */}
            {r.branch && (
              <Tip text={branchTag(r.branch).tip} focusable>
                <span className={branchTag(r.branch).className}>{branchTag(r.branch).text}</span>
              </Tip>
            )}
            {/* The file on step 1 was read as the carrier's non-renewal notice (spec C8 branch). */}
            {!r.branch && reading?.values?.documentType === 'non_renewal' && (
              <span className="tag t-red">Non-renewal notice</span>
            )}
            <span className={daysPill(r.daysLeft)}>{daysLabel(r.daysLeft)}</span>
          </div>
          <p className="m0 t13 muted">
            {[r.policyNumber, r.carrier ?? 'Carrier not in AMS360', r.line].join(' | ')} | Handled
            by {r.handledBy?.name ?? 'Unassigned'}
          </p>
        </div>
        <RecordActions
          r={r}
          role={role}
          meId={meId}
          // Not while the handoff waits on this AE: they answer it first (spec C7.4).
          noReassign={!!asking}
          onTakeOver={canTakeOver(r, role, meId) ? () => answers.takeOver([r]) : null}
          steps={stepsNow}
          onReassign={() => reassign.open([r])}
          onHand={() => hand.open(r)}
          onSteps={(what) =>
            what === 'check'
              ? stepsActions.current?.backToCheck()
              : setStepsAsk({ what, n: Date.now(), id: r.id })
          }
        />
      </div>

      {/* Rachy's one-click hands (the Record board): never automatic, the VA or Manager decides. */}
      {oneHand && (
        <div className="bnr info" role="status">
          <InfoIcon />
          <span>{oneHand.line}</span>
          <button
            type="button"
            className="btn btn-sm ml-auto"
            aria-haspopup="dialog"
            onClick={() => hand.open(r, { reason: oneHand.reason, note: oneHand.note })}
          >
            {oneHand.button}
          </button>
        </div>
      )}
      <IncreaseBox
        key={r.id}
        api={api}
        r={r}
        rule={view.increaseRule}
        premium={premium}
        role={role}
        onChanged={() => {
          // Handled by may have changed: the Record, its log and the steps' views are read again.
          setLiveLog(null);
          setLiveSteps(null);
          setStepView(null);
          void revalidator.revalidate();
        }}
      />

      {mine && (
        <AeHandoffBanner
          key={mine.id}
          h={mine}
          onAccept={() => asking && answers.accept(asking)}
          onSendBack={() => asking && answers.sendBack(asking)}
          onAddNote={() => {
            // The Notes card's own box (the Record board): brought into view, ready to type.
            const box = document.getElementById('note-input');
            box?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            box?.focus({ preventScroll: true });
          }}
          onFollowUp={(on) => answers.followUp(mine.id, on)}
          onFinish={withMe && stepsNow ? () => answers.finish(withMe, stepsNow, meInitials) : null}
          onPass={() => {
            const p = passRow(r, meId);
            if (p) answers.pass([p]);
          }}
        />
      )}
      {/* The AE's note after a send back, for whoever works it now (the Record board). */}
      {r.backFromAe && role !== 'Account Executive' && status.stage !== 'closed' && !r.handoff && (
        <BackFromAeBanner
          b={r.backFromAe}
          userId={meId}
          step={
            r.backFromAe.continueAtStep && stepsNow
              ? continueAtTitle(r.backFromAe.continueAtStep, stepsNow.amsStep)
              : null
          }
        />
      )}
      {r.handoff?.state === 'waiting' && r.handoff.to.id !== meId && (
        <WaitingBanner
          key={r.handoff.id}
          api={api}
          h={r.handoff}
          holder={r.handledBy}
          me={{ id: meId, manages: managesDepartment(role) }}
          onChanged={(text) => {
            setToast({ id: Date.now(), text });
            void revalidator.revalidate();
          }}
        />
      )}
      {withAe && (
        <div className="bnr info" role="status">
          <InfoIcon />
          <span>{withAe}</span>
        </div>
      )}
      {attention && (
        <div className="bnr attn">
          <AlertTriangleIcon />
          <span>{attention}</span>
        </div>
      )}
      {view.sync.lastOkAt && (
        <div className="bnr info">
          <InfoIcon />
          <span>Details from AMS360, synced {when(view.sync.lastOkAt, now)}.</span>
        </div>
      )}

      <div className="rgrid">
        <RecordSteps
          // A new handler (I'll reshop it, Assign to me) starts the steps again from the views read
          // again, so the new handler can work them at once (Codex on 242).
          key={`${r.id}:${r.handledBy?.id ?? 'none'}`}
          api={api}
          renewal={r}
          revision={current ? draftRevision(current) : 'no-fetch'}
          onCheck={setFixing}
          initial={steps ?? undefined}
          onStatusChange={() => {
            // The Record is read again, its Portal log with it, and the Dec page step's own view
            // from before (closed or open) is dropped for the one read again.
            setLiveLog(null);
            setStepView(null);
            void revalidator.revalidate();
          }}
          onView={onStepsView}
          ask={stepsAsk?.id === r.id ? stepsAsk : null}
          onAskDone={() => setStepsAsk(null)}
          me={{ id: meId, name: meName, manages: managesDepartment(role) }}
          actions={stepsActions}
          // Non-renewal step 4 (S17): Hand to AE with the reason filled in.
          onHandNonRenewal={(note) => hand.open(r, { reason: 'non_renewal', note })}
          first={
            // Keyed by renewal: going from one Record to another (the Open on a finished fetch's
            // message) starts a fresh step instead of keeping the last record's fetch. Keyed by
            // whether it is closed too: Close renewal and Reopen start it again from the view read
            // again, so a reopened Record offers Fetch and Upload PDF at once (Codex on 234).
            fetch && (
              <DecPageStep
                key={decStepKey(r.id, fetch)}
                api={api}
                renewal={r}
                initial={fetch}
                isAdmin={role === 'Admin'}
                onView={onStepView}
                pushed={pushed}
                onCheck={setFixing}
                previously={stepsNow?.decPage.previously ?? null}
                // Delete (S17): only while no step 2 to 6 is ticked.
                deletable={!!stepsNow && !Object.values(stepsNow.steps).some((s) => s.stamp)}
              />
            )
          }
        />
        <div className="rright">
          <Contact r={r} vehicles={reading?.values?.vehicleList ?? []} />
          <PriorVsRenewal
            r={r}
            now={now}
            reading={reading}
            rule={view.increaseRule}
            onFix={setFixing}
          />
          <PolicyDetails r={r} now={now} />
          {/* Auto has no mortgagee: lienholders and lessors get nothing at renewal (Rachy, Oct 28). */}
          {!isAutoLine(r.line) && <MortgageeCard steps={stepsNow} />}
          <FilesCard
            api={api}
            r={r}
            fetch={current}
            onChanged={() => {
              // The card's files come with the Dec page step's view: read it again, and the log.
              const at = Date.now();
              api.fetchView(r.id).then(
                (v) => setStepView({ id: r.id, at, value: v }),
                () => {},
              );
              reloadLog();
            }}
          />
          <NotesCard
            key={r.id}
            api={api}
            renewalId={r.id}
            refresh={notesTick}
            onChanged={reloadLog}
          />
          <PortalLogCard entries={liveOr(liveLog, r.id, loadedAt, log)} />
        </div>
      </div>
      {fixing && reading && (
        <FixDialog
          key={`${reading.documentId}-${fixing}`}
          api={api}
          renewal={r}
          reading={reading}
          field={fixing}
          decName={decName}
          onClose={() => setFixing(null)}
          onSaved={saved}
        />
      )}
      {reassign.element}
      {hand.element}
      {answers.element}
      {toast && (
        <Toast
          key={toast.id}
          text={toast.text}
          action={
            toast.undoFix
              ? { label: 'Undo', onClick: () => void undoFix(toast.undoFix!) }
              : undefined
          }
          shownMs={toast.undoFix ? 8000 : undefined}
        />
      )}
    </RecordLayout>
  );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="card">
      <div className="card-h">
        <h2 className="card-t">{title}</h2>
      </div>
      <div className="col g8" style={{ padding: 'var(--space-16)' }}>
        {children}
      </div>
    </section>
  );
}

function Facts({ items }: { items: [string, ReactNode][] }) {
  return (
    <dl className="pdl">
      {items.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

const dash = <span className="faint">Not in AMS360</span>;

function Phone({ label, value }: { label: string; value: string | null }): [string, ReactNode] {
  if (!value) return [label, dash];
  const href = telHref(value);
  return [
    label,
    href ? (
      <a className="lnk" href={href}>
        {value}
      </a>
    ) : (
      value
    ),
  ];
}

/**
 * Auto: the vehicles from the renewal dec page, one line each, the VIN's last 4 muted (Record board,
 * spec C7.4; S16). Null when there are none, so the row is left out as on the board.
 */
function vehiclesRow(
  line: string,
  vehicles: { name: string; vinEnd: string | null }[],
): [string, ReactNode] | null {
  if (!isAutoLine(line) || vehicles.length === 0) return null;
  return [
    'Vehicles',
    <span className="col g2">
      {vehicles.map((v, i) => (
        <span key={i}>
          {v.name}
          {v.vinEnd && <span className="muted"> VIN ending {v.vinEnd}</span>}
        </span>
      ))}
    </span>,
  ];
}

function Contact({
  r,
  vehicles,
}: {
  r: RenewalDetail;
  vehicles: { name: string; vinEnd: string | null }[];
}) {
  const c = r.contact;
  const cars = vehiclesRow(r.line, vehicles);
  if (!c) {
    return (
      <Card title="Insured contact">
        <p className="m0 t13 muted">The insured's details arrive with the next sync.</p>
        {cars && <Facts items={[cars]} />}
      </Card>
    );
  }
  const address = [
    c.addressLine1,
    c.addressLine2,
    [c.city, c.state].filter(Boolean).join(', '),
    c.zip,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <Card title="Insured contact">
      <Facts
        items={[
          ['Name', r.insuredFull],
          [
            'Email',
            c.email ? (
              <a className="lnk" href={`mailto:${c.email}`}>
                {c.email}
              </a>
            ) : (
              dash
            ),
          ],
          Phone({ label: 'Home phone', value: c.phoneHome }),
          Phone({ label: 'Cell', value: c.phoneCell }),
          Phone({ label: 'Business', value: c.phoneBusiness }),
          ['Mailing address', address || dash],
          ...(cars ? [cars] : []),
        ]}
      />
    </Card>
  );
}

/** The Check button, or the Edited tag with what was read and who changed it (spec C10). */
function FieldTag({
  f,
  canFix,
  onFix,
}: {
  f: ReadingField | null;
  canFix: boolean;
  onFix: (field: ReadingFixField) => void;
}) {
  if (!f) return null;
  if (f.check) {
    return canFix ? (
      <button
        type="button"
        className="chk-tag"
        aria-haspopup="dialog"
        aria-label={`Check ${fixLabels[f.field]}`}
        onClick={() => onFix(f.field)}
      >
        Check
      </button>
    ) : (
      <span className="chk-tag ro">Check</span>
    );
  }
  if (f.fix?.action === 'corrected') {
    return (
      <Tip text={editedTip(f)} focusable>
        <span className="tag t-grey">Edited</span>
      </Tip>
    );
  }
  return null;
}

/** A value the VA can open: the pencil button, or the plain value for someone who only looks. */
function FieldValue({
  f,
  canFix,
  onFix,
  big = false,
}: {
  f: ReadingField;
  canFix: boolean;
  onFix: (field: ReadingFixField) => void;
  big?: boolean;
}) {
  const shown = showValue(f.field, f.value) || 'Not read';
  const cls = [
    f.field === 'policyNumber' || f.field === 'totalPremium' ? 'mono' : '',
    big ? 'cv ink' : '',
    f.value === null ? 'faint' : '',
  ]
    .filter(Boolean)
    .join(' ');
  if (!canFix) return <span className={cls}>{shown}</span>;
  return (
    <button
      type="button"
      className={big ? 'fv cvb' : 'fv'}
      aria-haspopup="dialog"
      aria-label={`Edit ${fixLabels[f.field]}, ${shown}`}
      onClick={() => onFix(f.field)}
    >
      {!big && <PencilIcon className="ico s12 fv-p" />}
      <span className={cls}>{shown}</span>
      {big && <PencilIcon className="ico s12 fv-p" />}
    </button>
  );
}

/**
 * Prior term vs renewal (spec C10 compare panel, Record board): the prior premium from AMS360, the
 * renewal premium from the dec page and the change; the dec page values with Check and Edited; the
 * coverages next to the prior term's dec page, changed rows in amber. The renewal values open the
 * confirm or correct dialog; the prior values (AMS360) stay read only.
 */
function PriorVsRenewal({
  r,
  now,
  reading,
  rule,
  onFix,
}: {
  r: RenewalDetail;
  now: Date;
  reading: Reading | null;
  rule: IncreaseRule;
  onFix: (field: ReadingFixField) => void;
}) {
  // The dec page's premium once it is read (or typed), otherwise AMS360's renewed term.
  const renewal = renewalPremiumOf(r, reading);
  const diff = change({ ...r, renewalPremium: renewal?.value ?? null }, rule);
  const done = reading?.status === 'done';
  const failed = reading?.status === 'failed';
  const shown = done || failed;
  const canFix = !!reading?.canFix;
  const field = (k: ReadingFixField) => reading?.fields.find((f) => f.field === k) ?? null;
  const premium = shown ? field('totalPremium') : null;
  const toCheck = done && reading ? fieldsToCheck(reading) : [];
  const priorCoverages = reading?.prior?.status === 'done' ? reading.prior.coverages : null;
  const coverages = done && reading ? coverageRows(reading.coverages, priorCoverages) : [];
  const typed = failed && !!reading?.fields.some((f) => f.value !== null);

  return (
    <section className="card" aria-labelledby="pvr-t">
      <div className="card-h">
        <h2 id="pvr-t" className="card-t">
          Prior term vs renewal
        </h2>
      </div>
      <div className="cmp3">
        <div>
          <span className="t11 muted">Prior</span>
          <span className="mono cv text2">{money(r.priorPremium) || 'Not in AMS360'}</span>
        </div>
        <div>
          <span className="t11 muted row g4">
            Renewal
            <FieldTag f={premium} canFix={canFix} onFix={onFix} />
          </span>
          {premium && (canFix || premium.value !== null) ? (
            <FieldValue f={premium} canFix={canFix} onFix={onFix} big />
          ) : (
            <span className="mono cv ink">{renewal ? money(renewal.value) : 'Not known yet'}</span>
          )}
          {renewal && <span className="t11 faint">from the {renewal.source}</span>}
        </div>
        <div>
          <span className="t11 muted">Change</span>
          {diff ? (
            <span className={`mono cv ${diff.cls}`}>{diff.text}</span>
          ) : (
            <span className="cmp-na">Not known yet</span>
          )}
        </div>
      </div>
      <dl className="m0">
        {shown &&
          fixRows.map((k) => {
            const f = field(k)!;
            return (
              <div key={k} className="kv">
                <dt className="row g6">
                  {fixLabels[k]}
                  <FieldTag f={f} canFix={canFix} onFix={onFix} />
                </dt>
                <dd className="fvd">
                  <FieldValue f={f} canFix={canFix} onFix={onFix} />
                </dd>
              </div>
            );
          })}
        {coverages.map((c, i) => (
          <div key={`${c.name}-${i}`} className="kv">
            <dt>{c.name}</dt>
            <dd className="col g2" style={{ alignItems: 'flex-end', textAlign: 'right' }}>
              <span className={c.changed ? 'amber w5' : ''}>
                {c.renewal ?? 'Not on the renewal'}
              </span>
              {c.changed && c.prior && <span className="t12 muted">was {c.prior}</span>}
              {c.changed && !c.prior && <span className="t12 muted">not on the prior term</span>}
            </dd>
          </div>
        ))}
        <div className="kv">
          <dt>Renewed in AMS360</dt>
          <dd className="fvd">
            {r.renewedInAmsAt ? `Yes, seen ${when(r.renewedInAmsAt, now)}` : 'Not yet'}
          </dd>
        </div>
      </dl>
      {done && coverages.length > 0 && !priorCoverages && (
        <div className="cmpfoot">
          <InfoIcon className="ico s14" />
          <span>Coverages compare with the prior term once its dec page is read.</span>
        </div>
      )}
      {failed && !typed && (
        <div className="cmpfoot amber">
          <AlertCircleIcon className="ico s14" />
          <span>The portal could not read the dec page. Enter the values from the PDF.</span>
          {canFix && (
            <button
              type="button"
              className="btn btn-sm ml-auto"
              aria-haspopup="dialog"
              onClick={() => onFix('totalPremium')}
            >
              Enter values
            </button>
          )}
        </div>
      )}
      {toCheck.length > 0 && (
        <div className="cmpfoot amber">
          <AlertCircleIcon className="ico s14" />
          <span>{reviewLine(toCheck)}</span>
          {canFix && (
            <button
              type="button"
              className="btn btn-sm ml-auto"
              aria-haspopup="dialog"
              onClick={() => onFix(toCheck[0].field)}
            >
              Review
            </button>
          )}
        </div>
      )}
      {done && toCheck.length === 0 && (
        <div className="cmpfoot green">
          <CheckIcon className="ico s14" />
          <span>All fields confirmed</span>
        </div>
      )}
    </section>
  );
}

function PolicyDetails({ r, now }: { r: RenewalDetail; now: Date }) {
  const p = r.policy;
  const term = p.priorEffectiveDate
    ? `${longDate(p.priorEffectiveDate)} to ${longDate(r.expirationDate)}`
    : `Ends ${longDate(r.expirationDate)}`;
  return (
    <Card title="Policy details">
      <Facts
        items={[
          ['Policy #', <span className="mono">{r.policyNumber}</span>],
          ['Carrier', r.carrier ?? dash],
          ['Line', r.line],
          ['Term', term],
          ['Department', departmentName(p.departmentCode)],
          [
            'Bill method',
            r.agencyBilled
              ? 'Agency billed'
              : p.billMethod === 'P'
                ? 'Direct bill'
                : (p.billMethod ?? dash),
          ],
          ['AMS360 status', p.amsStatus ?? dash],
          ['Last seen in AMS360', when(p.lastSeenAt, now)],
        ]}
      />
    </Card>
  );
}
